// frontend/src/widgets/ResourceExpensesWidget.jsx
// Project:      Vision ANPR - Expenses (Advanced List)
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-09-17
// Updated:      2026-11-02 14:19 GMT (receipt View action-theme correction)
// Build Marker: RESOURCE_EXPENSES_AUTH_RECEIPT_PREVIEW_20261102
// Change Log (2026-11-02 GMT):
//   • Receipt View controls (private and legacy) use Vision Action button theme tokens.
//   • Preview receipts via authenticated axiosInstance blob requests, not public hyperlinks.
//   • In-app image/PDF viewer works on desktop/mobile without a new-tab token leak.
//   • Abort pending requests and revoke object URLs on close/unmount.
//   • Preserve existing mobile sizing, filters, layouts and legacy receipt handling.
//
// Description:
//   Advanced expenses list with:
//   • Column chooser (portaled) + drag/drop column reorder + per-user/tenant persistence.
//   • Filters: resource, status, category, date range (MTD/YTD + prev/next).
//   • Robust fetching via shared axiosInstance ONLY (no direct axios).
//   • Friendly-name enrichment via customer/resource maps.
//
// Notes:
//   • Uses shared axiosInstance from ../api/axiosInstance (canonical).
//   • Expects GET /api/expenses, /api/expenses/categories, /api/customers, /api/resources.
//
// Mobile Responsiveness (2026-09-09):
//   • Desktop retains the existing table, sortable/draggable headers and full toolbar.
//   • Mobile converts the same table rows into labelled expense cards; no duplicate
//     data render tree is mounted.
//   • Mobile shows one complete expense card per internal vertical scroll/snap.
//   • Dynamic height uses the proven Vision pattern: restore host sizing, clear
//     temporary viewport/root sizing, measure the first rendered card, apply that
//     exact viewport height, wait a second animation frame, then resize the first
//     width-matching fixed-height mobile host.
//   • Mobile toolbar stacks filters/controls and adds explicit Sort By + ▲/▼ controls
//     because the desktop table header is hidden.
//   • Column chooser retains visibility toggles and adds mobile ↑/↓ ordering controls;
//     desktop drag/drop header ordering remains unchanged.
//   • Existing filtering, range navigation, persistence, enrichment, totals and
//     expense/resource/customer API calls are unchanged.
//
// Operational Notes:
//   • One render tree is shared by desktop and mobile.
//   • Selected columns remain the source of truth for both desktop and mobile cards.
//
// Build Fix (2026-09-09 14:29 BST):
//   • Mobile data labels no longer perform any per-cell column-definition lookup.
//   • A module-level COLUMN_LABELS registry is built once from ALL_COLUMNS and
//     cells use COLUMN_LABELS[key] directly.
//   • There is no JavaScript identifier named `column` anywhere in this source.


import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import { filterAndSortList, getWidgetTitle } from "../utils/widgetUtils";
import ModalWrapper from "../components/ModalWrapper";

// ---- helpers ---------------------------------------------------------------
const gbDateTime = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
};

const toIso = (v) => (v ? new Date(v).toISOString() : undefined);

const firstOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const lastOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

const firstOfYear = (y = new Date().getFullYear()) => new Date(y, 0, 1);
const lastOfYear = (y = new Date().getFullYear()) => new Date(y, 11, 31, 23, 59, 59, 999);

const toYMD = (d) => d.toISOString().slice(0, 10);

const combineIdName = (id, name, fallbackLabel) => {
  if (id && name) return `${id} · ${name}`;
  if (id) return `${id} · ${fallbackLabel || ""}`.trim();
  return name || "";
};

// ---- column registry -------------------------------------------------------
const SCHEMA_VERSION = 3;
const storageKey = (uid, tid) => `anpr_expenses_columns_v${SCHEMA_VERSION}_${tid}_${uid}`;

const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "occurred_at", label: "Occurred" },
  { key: "amount", label: "Amount" },
  { key: "currency", label: "Currency" },
  { key: "category", label: "Category" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
  { key: "job_id", label: "Job (ID)" },
  { key: "customer_name", label: "Customer" },
  { key: "resource_name", label: "Resource" },
  { key: "receipt_url", label: "Receipt" },
  // raw IDs (hidden by default)
  { key: "customer_id", label: "Customer (ID)" },
  { key: "resource_id", label: "Resource (ID)" },
];

const COLUMN_LABELS = Object.freeze(
  Object.fromEntries(
    ALL_COLUMNS.map((columnDef) => [columnDef.key, columnDef.label])
  )
);

const DEFAULT_VISIBLE_ORDER = [
  "id",
  "occurred_at",
  "amount",
  "currency",
  "category",
  "description",
  "status",
  "customer_name",
  "resource_name",
  "receipt_url",
];

const SEARCH_FIELDS = [
  "id",
  "category",
  "description",
  "status",
  "job_id",
  "customer_id",
  "customer_name",
  "resource_id",
  "resource_name",
  "currency",
  "amount",
  "occurred_at",
];

// ---- Column chooser modal --------------------------------------------------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) =>
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const from = prev.indexOf(key);
      if (from < 0) return prev;

      const to = from + direction;
      if (to < 0 || to >= prev.length) return prev;

      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-auto pr-2">
        {order.map((key) => {
          const columnDef = allColumns.find((c) => c.key === key);
          if (!columnDef) return null;
          return (
            <div
              key={key}
              className="flex min-w-0 items-center justify-between gap-2 rounded border border-gray-200 px-2 py-2 text-sm"
            >
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                />
                <span className="min-w-0 break-words">{columnDef.label}</span>
              </label>

              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={order.indexOf(key) <= 0}
                  className="min-h-[36px] min-w-[36px] rounded border border-gray-200 bg-white px-2 font-bold disabled:opacity-40"
                  aria-label={`Move ${columnDef.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={order.indexOf(key) >= order.length - 1}
                  className="min-h-[36px] min-w-[36px] rounded border border-gray-200 bg-white px-2 font-bold disabled:opacity-40"
                  aria-label={`Move ${columnDef.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-between">
        <button
          onClick={resetToDefault}
          className="min-h-[44px] w-full sm:w-auto bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Reset to Default
        </button>
        <button
          onClick={onClose}
          className="min-h-[44px] w-full sm:w-auto bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

// ---- main ------------------------------------------------------------------
const ResourceExpensesWidget = ({
  resources = [],
  selectedResourceId = "",
  // compatibility: parent may pass `resourceId` (alias)
  resourceId = null,
}) => {
  // filters
  const [rid, setRid] = useState(selectedResourceId || resourceId || "");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // dates
  const [dtFrom, setDtFrom] = useState("");
  const [dtTo, setDtTo] = useState("");
  const [rangeMode, setRangeMode] = useState(""); // "", "MTD", "YTD"

  // data/state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const { refreshKey } = useDashboardRefresh();

  // Expense receipts are protected by the backend. Never navigate directly to
  // receipt_url: a normal browser link does not attach Vision's bearer token.
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);
  const [receiptError, setReceiptError] = useState("");
  const receiptRequestRef = useRef(null);
  const receiptObjectUrlRef = useRef(null);

  const closeReceiptPreview = useCallback(() => {
    if (receiptRequestRef.current) {
      receiptRequestRef.current.abort();
      receiptRequestRef.current = null;
    }
    if (receiptObjectUrlRef.current) {
      URL.revokeObjectURL(receiptObjectUrlRef.current);
      receiptObjectUrlRef.current = null;
    }
    setReceiptPreview(null);
    setReceiptLoadingId(null);
  }, []);

  useEffect(() => () => {
    if (receiptRequestRef.current) receiptRequestRef.current.abort();
    if (receiptObjectUrlRef.current) URL.revokeObjectURL(receiptObjectUrlRef.current);
  }, []);

  useEffect(() => {
    if (!receiptPreview) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeReceiptPreview();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [receiptPreview, closeReceiptPreview]);

  const viewReceipt = useCallback(async (expense) => {
    if (!expense?.id || !expense?.receipt_url) return;
    closeReceiptPreview();
    setReceiptError("");
    setReceiptLoadingId(expense.id);
    const controller = new AbortController();
    receiptRequestRef.current = controller;
    try {
      // Only the canonical private receipt path uses this endpoint.
      if (String(expense.receipt_url) !== `/api/expenses/${expense.id}/receipt/view`) {
        throw new Error("Receipt is not in private storage");
      }
      const response = await axiosInstance.get(`expenses/${expense.id}/receipt/view`, {
        responseType: "blob",
        signal: controller.signal,
        timeout: 30000,
      });
      if (controller.signal.aborted) return;
      const blob = response.data;
      const mime = (blob.type || response.headers?.["content-type"] || "").split(";")[0].toLowerCase();
      if (!(["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(mime))) {
        throw new Error("Unsupported receipt format returned by server");
      }
      const objectUrl = URL.createObjectURL(blob);
      receiptObjectUrlRef.current = objectUrl;
      setReceiptPreview({ url: objectUrl, mime, expenseId: expense.id });
    } catch (error) {
      if (controller.signal.aborted) return;
      const detail = error?.response?.status === 401
        ? "Session expired. Sign in again to view this receipt."
        : error?.response?.status === 403
          ? "You do not have permission to view this receipt."
          : error?.response?.status === 404
            ? "Receipt file not found."
            : error?.message || "Unable to open receipt.";
      setReceiptError(detail);
    } finally {
      if (receiptRequestRef.current === controller) {
        receiptRequestRef.current = null;
        setReceiptLoadingId(null);
      }
    }
  }, [closeReceiptPreview]);

  // fallback maps
  const [customerMap, setCustomerMap] = useState({});
  const [resourceMap, setResourceMap] = useState({});
  const [categories, setCategories] = useState([]);

  // local resource options for dropdown
  const [resourceOptions, setResourceOptions] = useState([]);

  // keep rid synced if parent changes selectedResourceId/resourceId
  useEffect(() => {
    const incoming = resourceId ?? selectedResourceId;
    if (incoming !== undefined && incoming !== null && incoming !== "") {
      setRid(Number(incoming));
    }
  }, [resourceId, selectedResourceId]);

  // fetch resources for dropdown (robust to backend shape)
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("resources?limit=2000")
      .then((r) => {
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : r.data?.items || [];
        setResourceOptions(arr);
      })
      .catch(() => {
        if (!cancelled) setResourceOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resourceLabel = (r) =>
    r?.name ||
    [r?.first_name, r?.last_name].filter(Boolean).join(" ").trim() ||
    r?.identifier ||
    r?.email ||
    `Resource ${r?.id}`;

  // persisted columns
  const userKey = useMemo(() => {
    try {
      const tok =
        localStorage.getItem("anpr_token") ||
        localStorage.getItem("token") ||
        "";
      const [, payload] = tok.split(".");
      const p =
        JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) || {};
      return { uid: p.sub || p.user_id || "u", tid: p.tenant_id || p.tid || "t" };
    } catch {
      return { uid: "u", tid: "t" };
    }
  }, []);
  const persistKey = useMemo(() => storageKey(userKey.uid, userKey.tid), [userKey]);

  const [visibilityMap, setVisibilityMap] = useState(() => {
    let map = {};
    try {
      map =
        JSON.parse(localStorage.getItem(persistKey) || "{}")?.visibilityMap ||
        {};
    } catch {}
    ALL_COLUMNS.forEach((c) => {
      if (typeof map[c.key] === "undefined")
        map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key);
    });
    return map;
  });

  const [order, setOrder] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(persistKey) || "{}");
      if (Array.isArray(parsed.order) && parsed.order.length) {
        const saved = parsed.order;
        const missing = ALL_COLUMNS.map((c) => c.key).filter(
          (k) => !saved.includes(k)
        );
        return [...saved, ...missing];
      }
    } catch {}
    const rest = ALL_COLUMNS.map((c) => c.key).filter(
      (k) => !DEFAULT_VISIBLE_ORDER.includes(k)
    );
    return [...DEFAULT_VISIBLE_ORDER, ...rest];
  });

  const [chooserOpen, setChooserOpen] = useState(false);

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstRecordRef = useRef(null);

  // sort + dnd
  const [sortField, setSortField] = useState("occurred_at");
  const [ascending, setAscending] = useState(false);
  const visibleKeys = useMemo(
    () => order.filter((k) => visibilityMap[k]),
    [order, visibilityMap]
  );

  const mobileSortKeys = useMemo(() => {
    if (visibleKeys.includes(sortField)) return visibleKeys;
    return [sortField, ...visibleKeys.filter((key) => key !== sortField)];
  }, [sortField, visibleKeys]);

  const dragIndexRef = useRef(null);
  const onHeaderDragStart = (e, key) => {
    dragIndexRef.current = visibleKeys.indexOf(key);
    e.dataTransfer.effectAllowed = "move";
  };
  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);
    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);
    dragIndexRef.current = null;
  };

  const handleSort = (field) => {
    if (field === sortField) setAscending((a) => !a);
    else {
      setSortField(field);
      setAscending(true);
    }
  };
  const sortIndicator = (field) =>
    field === sortField ? (ascending ? " ▲" : " ▼") : null;

  // persist layout
  useEffect(() => {
    localStorage.setItem(
      persistKey,
      JSON.stringify({ order, visibilityMap, v: SCHEMA_VERSION })
    );
  }, [order, visibilityMap, persistKey]);

  // maps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cust, res] = await Promise.allSettled([
          axiosInstance.get("customers?limit=2000"),
          axiosInstance.get("resources?limit=2000"),
        ]);
        if (cancelled) return;

        if (cust.status === "fulfilled") {
          const m = {};
          (cust.value.data || []).forEach((c) => {
            m[c.id] = c.name || c.nickname || `Customer #${c.id}`;
          });
          setCustomerMap(m);
        }

        if (res.status === "fulfilled") {
          const m = {};
          (res.value.data || []).forEach((r) => {
            const nm =
              [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
              r.email ||
              `Resource #${r.id}`;
            m[r.id] = nm;
          });
          setResourceMap(m);
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // categories
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("expenses/categories");
        if (!cancelled) setCategories(Array.isArray(res.data) ? res.data : []);
      } catch {
        // fallback from current rows
        const uniq = Array.from(
          new Set((rows || []).map((r) => r.category).filter(Boolean))
        ).sort();
        if (!cancelled) setCategories(uniq);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // once

  // fetch expenses
  const refresh = useCallback(async () => {
    setErrMsg("");
    setLoading(true);
    try {
      const params = {
        limit: 500,
        resource_id: rid || undefined,
        status: status || undefined,
        category: category || undefined,
        dt_from: toIso(dtFrom),
        dt_to: dtTo ? new Date(`${dtTo}T23:59:59`).toISOString() : undefined,
      };
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      );

      const res = await axiosInstance.get("expenses", { params: clean });
      const arr = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setRows(arr);

      if (!categories.length) {
        const uniq = Array.from(
          new Set(arr.map((r) => r.category).filter(Boolean))
        ).sort();
        setCategories(uniq);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.response?.data || e?.message || String(e);
      setErrMsg(String(msg));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rid, status, category, dtFrom, dtTo, categories.length]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, status, category, dtFrom, dtTo]);

  // enrich with friendly names *reactively*
  const viewRows = useMemo(() => {
    return (rows || []).map((e) => ({
      ...e,
      customer_name:
        e.customer_name ||
        (e.customer_id ? customerMap[e.customer_id] : "") ||
        (e.customer_id ? `Customer #${e.customer_id}` : ""),
      resource_name:
        e.resource_name ||
        (e.resource_id ? resourceMap[e.resource_id] : "") ||
        (e.resource_id ? `Resource #${e.resource_id}` : ""),
    }));
  }, [rows, customerMap, resourceMap]);

  // client search/sort + totals
  const processed = useMemo(
    () =>
      filterAndSortList(
        viewRows,
        searchQuery,
        SEARCH_FIELDS,
        sortField,
        ascending
      ),
    [viewRows, searchQuery, sortField, ascending]
  );

  const total = useMemo(() => {
    return processed.reduce((s, r) => {
      const n = typeof r.amount === "number" ? r.amount : parseFloat(r.amount);
      return Number.isFinite(n) ? s + n : s;
    }, 0);
  }, [processed]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstRecord = firstRecordRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstRecord = firstRecordRef.current;

        if (currentFirstRecord) {
          const recordHeight = Math.ceil(
            Math.max(
              currentFirstRecord.getBoundingClientRect().height || 0,
              currentFirstRecord.scrollHeight || 0
            )
          );

          if (Number.isFinite(recordHeight) && recordHeight > 0) {
            viewport.style.height = `${recordHeight}px`;
            viewport.style.minHeight = `${recordHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstRecord) {
      resizeObserver.observe(firstRecord);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    ascending,
    chooserOpen,
    errMsg,
    loading,
    processed.length,
    searchQuery,
    sortField,
    visibleKeys,
  ]);

  const resetToDefault = () => {
    const map = {};
    ALL_COLUMNS.forEach(
      (c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key))
    );
    setVisibilityMap(map);

    const rest = ALL_COLUMNS.map((c) => c.key).filter(
      (k) => !DEFAULT_VISIBLE_ORDER.includes(k)
    );
    setOrder([...DEFAULT_VISIBLE_ORDER, ...rest]);

    setSortField("occurred_at");
    setAscending(false);
  };

  const title = getWidgetTitle ? getWidgetTitle("expenses") : "Expenses";

  // Quick date chips: MTD / YTD / Prev / Next
  const applyMTD = () => {
    const s = firstOfMonth(new Date());
    const e = lastOfMonth(new Date());
    setDtFrom(toYMD(s));
    setDtTo(toYMD(e));
    setRangeMode("MTD");
  };

  const applyYTD = (year = new Date().getFullYear()) => {
    const s = firstOfYear(year);
    const e = lastOfYear(year);
    setDtFrom(toYMD(s));
    setDtTo(toYMD(e));
    setRangeMode("YTD");
  };

  const shiftRange = (dir) => {
    if (!rangeMode) return;
    if (rangeMode === "MTD") {
      const base = dtFrom ? new Date(dtFrom) : firstOfMonth();
      const s = new Date(base.getFullYear(), base.getMonth() + dir, 1);
      const e = new Date(s.getFullYear(), s.getMonth() + 1, 0, 23, 59, 59, 999);
      setDtFrom(toYMD(s));
      setDtTo(toYMD(e));
    } else if (rangeMode === "YTD") {
      const y0 = (dtFrom ? new Date(dtFrom) : firstOfYear()).getFullYear();
      applyYTD(y0 + dir);
    }
  };

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-expenses-table {
            display: block;
            width: 100%;
          }

          .vx-expenses-table thead {
            display: none;
          }

          .vx-expenses-table tbody {
            display: grid;
            gap: 8px;
            width: 100%;
          }

          .vx-expense-row {
            display: grid;
            width: 100%;
            min-width: 0;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #fff !important;
            overflow: hidden;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-expense-cell {
            display: grid;
            grid-template-columns: minmax(88px, 0.42fr) minmax(0, 1fr);
            gap: 10px;
            align-items: start;
            width: 100%;
            min-width: 0;
            padding: 8px 10px !important;
            border-bottom: 1px solid #f3f4f6;
            overflow-wrap: anywhere;
          }

          .vx-expense-cell:last-child {
            border-bottom: 0;
          }

          .vx-expense-cell::before {
            content: attr(data-label);
            color: #6b7280;
            font-size: 10px;
            line-height: 14px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.2px;
          }

          .vx-expenses-records {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-expenses-records::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>
      {/* Accent toolbar (filters) */}
      <div className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
        <span className="text-sm font-semibold">{title}</span>

        <div className="grid w-full grid-cols-1 gap-2 md:flex md:w-auto md:flex-wrap md:items-center">
          {/* Resource */}
          <select
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-auto"
            value={rid}
            onChange={(e) => setRid(e.target.value ? Number(e.target.value) : "")}
            title="Filter by resource"
          >
            <option value="">All resources</option>
            {(resourceOptions.length ? resourceOptions : resources || []).map((r) => (
              <option key={r.id} value={r.id}>
                {resourceLabel(r)}
              </option>
            ))}
          </select>

          {/* Status */}
          <select
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-auto"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            title="Filter by status"
          >
            <option value="">Any status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>

          {/* Category */}
          <select
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-auto"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            title="Filter by category"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          {/* Date range */}
          <input
            type="date"
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-auto"
            value={dtFrom}
            onChange={(e) => {
              setDtFrom(e.target.value);
              setRangeMode("");
            }}
            title="From date"
          />
          <span className="hidden md:inline">–</span>
          <input
            type="date"
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-auto"
            value={dtTo}
            onChange={(e) => {
              setDtTo(e.target.value);
              setRangeMode("");
            }}
            title="To date"
          />

          {/* Quick chips */}
          <div className="grid grid-cols-4 gap-1 md:flex">
            <button
              type="button"
              onClick={applyMTD}
              className="min-h-[44px] bg-white/90 text-black text-xs px-2 py-1 rounded md:min-h-0"
              title="Month to date"
            >
              MTD
            </button>
            <button
              type="button"
              onClick={() => applyYTD()}
              className="min-h-[44px] bg-white/90 text-black text-xs px-2 py-1 rounded md:min-h-0"
              title="Year to date"
            >
              YTD
            </button>
            <button
              type="button"
              onClick={() => shiftRange(-1)}
              disabled={!rangeMode}
              className="min-h-[44px] bg-white/90 text-black disabled:opacity-60 text-xs px-2 py-1 rounded md:min-h-0"
              title={rangeMode === "YTD" ? "Previous year" : "Previous month"}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => shiftRange(1)}
              disabled={!rangeMode}
              className="min-h-[44px] bg-white/90 text-black disabled:opacity-60 text-xs px-2 py-1 rounded md:min-h-0"
              title={rangeMode === "YTD" ? "Next year" : "Next month"}
            >
              Next
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search…"
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-52"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Mobile sort controls (desktop sorting remains on table headers) */}
          <div className="grid grid-cols-[minmax(0,1fr)_44px] gap-2 md:hidden">
            <select
              className="min-h-[44px] min-w-0 rounded border border-white bg-white px-2 text-sm text-black"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              aria-label="Sort expenses by"
            >
              {mobileSortKeys.map((key) => {
                const columnDef = ALL_COLUMNS.find((c) => c.key === key);
                if (!columnDef) return null;
                return (
                  <option key={key} value={key}>
                    Sort by: {columnDef.label}
                  </option>
                );
              })}
            </select>

            <button
              type="button"
              onClick={() => setAscending((prev) => !prev)}
              className="min-h-[44px] rounded bg-white/90 px-2 text-sm font-bold text-black"
              aria-label={ascending ? "Sort ascending" : "Sort descending"}
            >
              {ascending ? "▲" : "▼"}
            </button>
          </div>

          {/* Column chooser + Refresh + Totals */}
          <button
            type="button"
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
          >
            Column Chooser
          </button>

          <button
            type="button"
            onClick={refresh}
            className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
          >
            Refresh
          </button>

          <span className="break-words text-xs opacity-90">
            {loading ? "Loading…" : `${processed.length} item(s) • total ${total.toFixed(2)}`}
          </span>
        </div>
      </div>

      {/* Error */}
      {errMsg ? (
        <div className="break-words px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200">
          {errMsg}
        </div>
      ) : null}

      {/* Table */}
      <div ref={recordsViewportRef} className="vx-expenses-records h-auto w-full overflow-visible md:h-full md:overflow-auto">
        <table className="vx-expenses-table w-full text-sm text-left text-gray-700">
          <thead>
            <tr className="bg-accent text-white select-none">
              {visibleKeys.map((key) => {
                const columnDef = ALL_COLUMNS.find((c) => c.key === key);
                if (!columnDef) return null;
                return (
                  <th
                    key={key}
                    className="px-4 py-2 cursor-move sticky top-0 z-10 bg-accent"
                    draggable
                    onDragStart={(e) => onHeaderDragStart(e, key)}
                    onDragOver={onHeaderDragOver}
                    onDrop={(e) => onHeaderDrop(e, key)}
                    onClick={() => handleSort(key)}
                    title="Drag to reorder • Click to sort"
                  >
                    {columnDef.label}
                    {sortIndicator(key)}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={visibleKeys.length}
                  className="text-center py-4 text-gray-500 italic"
                >
                  Loading expenses…
                </td>
              </tr>
            ) : processed.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleKeys.length}
                  className="text-center py-4 text-gray-500 italic"
                >
                  No expenses found.
                </td>
              </tr>
            ) : (
              processed.map((e, index) => (
                <tr
                  key={e.id}
                  ref={index === 0 ? firstRecordRef : null}
                  className="vx-expense-row odd:bg-gray-50 even:bg-white border-b border-gray-100"
                >
                  {visibleKeys.map((key) => {
                    const renderCell = () => {
                      if (key === "occurred_at") return gbDateTime(e.occurred_at);

                      if (key === "amount") {
                        const n =
                          typeof e.amount === "number"
                            ? e.amount
                            : parseFloat(e.amount);
                        return Number.isFinite(n) ? n.toFixed(2) : String(e.amount ?? "");
                      }

                      if (key === "status") {
                        const s = String(e.status || "").toLowerCase();
                        const bg =
                          s === "approved"
                            ? "#16a34a22"
                            : s === "submitted"
                            ? "#f59e0b22"
                            : s === "rejected"
                            ? "#b91c1c22"
                            : s === "paid"
                            ? "#0ea5e922"
                            : "#6b728022";
                        const statusColor =
                          s === "approved"
                            ? "#166534"
                            : s === "submitted"
                            ? "#92400e"
                            : s === "rejected"
                            ? "#7f1d1d"
                            : s === "paid"
                            ? "#075985"
                            : "#374151";
                        return (
                          <span
                            style={{
                              background: bg,
                              color: statusColor,
                              padding: "2px 8px",
                              borderRadius: 999,
                              fontWeight: 600,
                            }}
                          >
                            {e.status || "—"}
                          </span>
                        );
                      }

                      if (key === "receipt_url") {
                        if (e.receipt_url && e.receipt_url !== `/api/expenses/${e.id}/receipt/view`) {
                          // Preserve legacy URLs without routing them through the
                          // new private receipt endpoint. Do not allow JS URLs.
                          const legacy = String(e.receipt_url);
                          return /^(https?:\/\/|\/)/i.test(legacy) ? (
                            <a href={legacy} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[32px] items-center justify-center rounded bg-btnActionNormal px-3 py-1 text-xs font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick md:min-h-0">View</a>
                          ) : "—";
                        }
                        return e.receipt_url ? (
                          <button
                            type="button"
                            className="inline-flex min-h-[32px] items-center justify-center rounded bg-btnActionNormal px-3 py-1 text-xs font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
                            onClick={() => viewReceipt(e)}
                            disabled={receiptLoadingId !== null}
                            aria-label={`View receipt for expense ${e.id}`}
                          >
                            {receiptLoadingId === e.id ? "Opening…" : "View"}
                          </button>
                        ) : (
                          "—"
                        );
                      }

                      if (key === "customer_name") {
                        return (
                          e.customer_name ||
                          combineIdName(
                            e.customer_id,
                            customerMap[e.customer_id],
                            "Customer"
                          )
                        );
                      }

                      if (key === "resource_name") {
                        return (
                          e.resource_name ||
                          combineIdName(
                            e.resource_id,
                            resourceMap[e.resource_id],
                            "Resource"
                          )
                        );
                      }

                      return e[key] ?? "";
                    };

                    return (
                      <td
                        key={key}
                        data-label={COLUMN_LABELS[key] || key}
                        className="vx-expense-cell px-4 py-2"
                      >
                        {renderCell()}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {receiptError ? (
        <div role="alert" className="px-4 py-2 text-sm text-red-700 bg-red-50 border-t border-red-200">
          {receiptError}
          <button type="button" onClick={() => setReceiptError("")} className="ml-3 underline">Dismiss</button>
        </div>
      ) : null}

      {receiptPreview && createPortal(
        <div
          role="presentation"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-2 sm:p-5"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeReceiptPreview();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Receipt for expense ${receiptPreview.expenseId}`}
            className="flex h-[95dvh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b p-3">
              <strong className="text-sm text-gray-900">Expense #{receiptPreview.expenseId} — Receipt</strong>
              <button
                type="button"
                className="rounded bg-btnGeneralNormal px-4 py-2 text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                onClick={closeReceiptPreview}
              >
                Close
              </button>
            </div>
            {receiptPreview.mime === "application/pdf" ? (
              <iframe
                title={`Receipt PDF for expense ${receiptPreview.expenseId}`}
                src={receiptPreview.url}
                className="min-h-0 w-full flex-1 border-0"
              />
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-gray-100 p-2">
                <img
                  src={receiptPreview.url}
                  alt={`Receipt for expense ${receiptPreview.expenseId}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Column chooser */}
      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={ALL_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={resetToDefault}
      />
    </div>
  );
};

export default ResourceExpensesWidget;
